import type { FeedbackContext } from "@estelle/domain";

import { useCurrentUser } from "@/auth/queries";
import { useSectionSearch } from "@/routes/search";
import { useActiveSection } from "@/routes/useActiveSection";

/**
 * What #319 attaches to every submission automatically: where the reporter
 * was, never what they typed on that screen.
 *
 * The build's own version, not an API call — Cloudflare Pages has not been
 * wired to stamp one in yet (that is CI work, not this ticket's), so `dev`
 * is the honest answer until `VITE_APP_VERSION` is set at build time, the
 * same way `APP_VERSION` already is for the API image.
 */
const APP_VERSION =
  (import.meta.env.VITE_APP_VERSION as string | undefined) ?? "dev";

/**
 * A function that reads the current screen at the moment it is called,
 * rather than a value computed once — the reporter can sit on the dialog for
 * a minute before sending, and `time` and the viewport should describe the
 * moment they hit Send, not the moment they opened it.
 */
export function useFeedbackContext(): () => FeedbackContext {
  const { module, section } = useActiveSection();
  const search = useSectionSearch();
  const { data: user } = useCurrentUser();

  return () => ({
    url: `${window.location.pathname}${window.location.search}`,
    module: module.label,
    section: section.label,
    // The drawer or sheet that is open over the section (#498). The id only —
    // see `FeedbackContext.record`.
    record: search.record,
    filter: search.filter,
    sort: search.sort,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    browser: navigator.userAgent,
    appVersion: APP_VERSION,
    reporter: user?.email ?? user?.name ?? "unknown",
    time: new Date().toISOString(),
  });
}
